import { motion } from "framer-motion";
import { Asterisk } from "./Brand";
import { IMAGES, STATS } from "@/data/content";
import { fadeUp, stagger } from "@/lib/motion";

const TALK_ABOUT = [
  { q: "Dating somebody you know you should've left alone?", answer: "We can talk about it." },
  { q: "Outgrew a friendship?", answer: "Let's get into it." },
  { q: "The internet collectively lost its damn mind over something?", answer: "Oh, we're definitely talking about that." },
];

const reveal = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.215, 0.61, 0.355, 1] } },
};

export default function AboutSection() {
  return (
    <section id="about" data-testid="about-section" className="scroll-mt-24 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="grid grid-cols-1 gap-4 lg:grid-cols-12"
        >
          <motion.div
            variants={reveal}
            data-testid="about-synopsis-card"
            className="relative overflow-hidden rounded-3xl border border-white/10 bg-panel lg:col-span-7"
          >
            <img
              src={IMAGES.microphone}
              alt="Morgan Chanel against the city skyline at night"
              className="absolute inset-0 h-full w-full object-cover opacity-25"
            />
            <div className="relative flex h-full flex-col justify-between p-8 sm:p-10 lg:p-12">
              <div>
                <p className="mb-5 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-cherry">
                  <Asterisk className="h-3.5 w-3.5" /> About CGD
                </p>
                <h2 className="font-display text-3xl font-bold leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl">
                  Some conversations are{" "}
                  <span className="font-garamond italic text-cherry">too good</span> to
                  stay in the group chat.
                </h2>
                <p className="mt-6 max-w-lg text-base leading-relaxed text-white/70 sm:text-lg">
                  City Girls Decoded was created by Morgan Chanel for people who
                  can laugh at the mess, own their shit, learn from it, and still
                  have something to say afterward. Born from Morgan's love of
                  storytelling and those brutally honest conversations that
                  happen between friends, CGD is a space where real life gets
                  decoded in real time.
                </p>
              </div>
              <div className="mt-10">
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cherry">
                  That's the heart of CGD
                </p>
                <p className="mt-3 font-garamond text-2xl italic text-white/85 sm:text-3xl">
                  "You don't have to be perfect to have something worth saying."
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={reveal}
            data-testid="about-stats-container"
            className="grid grid-cols-2 gap-4 lg:col-span-5 lg:grid-cols-1"
          >
            {STATS.map((s) => (
              <div
                key={s.label}
                data-testid="about-stat-card"
                className="group flex h-full flex-col justify-between rounded-3xl border border-white/10 bg-panel p-6 transition-colors duration-300 hover:border-cherry/50 sm:p-8"
              >
                <p className="font-display text-3xl font-extrabold tracking-tight text-white transition-colors group-hover:text-cherry sm:text-4xl">
                  {s.value}
                </p>
                <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50">
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:col-span-12">
            {TALK_ABOUT.map((t, i) => (
              <motion.div
                key={t.answer}
                variants={reveal}
                data-testid={`about-topic-card-${i + 1}`}
                className="h-full rounded-3xl border border-white/10 bg-panel p-7 transition-colors duration-300 hover:border-cherry/50"
              >
                <p className="font-garamond text-xl italic leading-snug text-white/75">
                  "{t.q}"
                </p>
                <p className="mt-4 font-display text-lg font-bold text-cherry">{t.answer}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}
          className="mt-14 max-w-3xl font-garamond text-2xl italic leading-snug text-white/80 sm:text-3xl lg:text-4xl"
        >
          "Come for the conversation. Stay because somebody finally said it."
        </motion.p>
      </div>
    </section>
  );
}
